import { Box, Container, Divider, Flex } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { CheckoutTheme } from '@getjust/leaf';

import CartDetail from '$components/CartDetail';
import { Header } from '$components/Header';
import { LoadingWrapper } from '$components/LoadingWrapper';
import { ProgressBar } from '$components/ProgressBar';
import { CartSkeleton } from '$src/components/CartSkeleton';
import ExitIntentPopup from '$src/components/ExitIntentPopup';
import { REFS_MAP } from '$src/constants';
import { ExitIntentContext } from '$src/context/ExitIntentContext';
import { useThemeContext } from '$src/context/ThemeContext';
import { useExitIntent } from '$src/hooks/client/useExitIntent';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { useCart, useFeatureFlag, useShop } from '$src/hooks/queries';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useLoadingAtom, usePaidLoadingAtom } from '$src/hooks/state/useLoadingAtom';
import { getScrollbarStyles, getThemeColors } from '$src/utils/themeColors';

const collapseHeader = keyframes`
  0% { height: 5.5rem; }
  100% { height: 4rem; }
`;

const expandHeader = keyframes`
  0% { height: 4rem; }
  100% { height: 5.5rem; }
`;

const collapseCartSummary = keyframes`
  from { height: 100%; }
  to { height: 0; }
`;

const expandCartSummary = keyframes`
  from { height: 0; }
  to { height: 100%; }
`;

const fadeInCartSummary = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const fadeOutCartSummary = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const isClassDivierStyle = {
  border: '0.8px solid var(--border-gradient)',
  height: '100%',
  top: 0,
  opacity: 1,
};

type Props = {
  children: React.ReactNode;
};

export const Layout = ({ children }: Props) => {
  const { isLargerThan750, isLargerThan900, isMobile } = useIsDesktop();
  const { loading } = useLoadingAtom();
  const { paidLoading } = usePaidLoadingAtom();
  const { data: cart, status: useCartStatus, isLoading: cartLoading } = useCart();
  const { data: shop } = useShop();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const { themeName } = useThemeContext();
  const brandingPreview = useROBrandingPreviewAtom();
  const colors = useMemo(() => getThemeColors(themeName), [themeName]);
  const background = brandingPreview?.brandStyles?.background ?? shop?.settings?.brandStyles?.background;
  const isTransparentCustom = themeName === 'custom' && background === 'transparent';
  const isGlassy = themeName === 'glassy' || isTransparentCustom;

  const scrollbarStyles = useMemo(() => getScrollbarStyles(themeName), [themeName]);

  const isClassicOrCustom = themeName === 'classic' || themeName === 'custom';

  // Theme-aware background colors
  const formBgMap: Record<CheckoutTheme, string> = {
    aurora: colors.bg,
    black: colors.bgSecondary,
    glassy: colors.bgSecondary,
    soft: colors.bg,
    classic: colors.bg,
    custom: colors.bgSecondary,
  };
  const cartBgMap: Record<CheckoutTheme, string> = {
    aurora: colors.bgSecondary,
    black: colors.bg,
    glassy: colors.bgTertiary,
    soft: colors.cardBg,
    classic: colors.cardBg,
    custom: colors.cardBg,
  };

  const glassyColors = getThemeColors('glassy');
  const formBg = isTransparentCustom ? glassyColors.bgSecondary : formBgMap[themeName];
  const cartBg = isTransparentCustom ? glassyColors.bgTertiary : cartBgMap[themeName];
  const dividerColor = colors.border;
  const basePaddingBottom = isLargerThan900 ? 12 : isLargerThan750 ? 8 : 2;
  const contentPaddingBottom = isGlassy ? basePaddingBottom + 4 : basePaddingBottom;

  // A/B test: exit intent popup
  // Control group: popup disabled | Enabled variant: popup shown
  const { data: exitIntentVariant, isPending: isExitIntentFlagPending } = useFeatureFlag('exit_intent_popup');
  // Treat as disabled while loading to avoid flash, and only enable if variant is 'enabled'
  const isExitIntentEnabled = !isExitIntentFlagPending && exitIntentVariant === 'enabled';

  const exitIntent = useExitIntent({ isFeatureEnabled: isExitIntentEnabled, shuffleOptions: true });

  const portalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // update tab title
    if (shop?.name && shop.name !== document.title) {
      document.title = shop?.name;
    }
  }, [shop?.name]);

  useEffect(() => {
    if (portalRef.current) {
      REFS_MAP.set('portal', portalRef);
    }
  }, []);

  useEffect(() => {
    // slide animation for mobiles
    if (!portalRef.current) return;
    const myTimeout = setTimeout(() => {
      portalRef.current?.classList.add('slideIn');
    }, 200);
    return () => clearTimeout(myTimeout);
  }, []);

  const [scrollPercentage, setScrollPercentage] = useState<number>(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isCartSummaryCollapsed, setIsCartSummaryCollapsed] = useState(false);

  useEffect(() => {
    const scrollableDiv = mainRef.current;
    if (!scrollableDiv) return;

    function handleScroll() {
      if (!scrollableDiv) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
      const maxScroll = scrollHeight - clientHeight;
      const scrolled = (scrollTop / maxScroll) * 100;
      setScrollPercentage(scrolled);

      setIsHeaderCollapsed(scrolled >= 2);
      setIsCartSummaryCollapsed(scrolled >= 2);
    }

    scrollableDiv.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollableDiv.removeEventListener('scroll', handleScroll);
  }, []);

  const showMobileBackground = canUseCheckoutFeature('show_mobile_background_image') && isMobile;

  const showProgressBar = useMemo(() => {
    if (loading || cartLoading) return false;
    if (!isMobile) return scrollPercentage >= 10;

    return showMobileBackground ? scrollPercentage >= 6 : scrollPercentage >= 3;
  }, [scrollPercentage, showMobileBackground, isMobile, loading, cartLoading]);

  const headerHeight = useMemo(() => {
    if (!isMobile) return '3.75rem';
    if (!showMobileBackground) return '4rem';

    return '5.5rem';
  }, [isMobile, showMobileBackground]);

  const animations = useMemo(() => {
    if (!showMobileBackground) return { header: {}, cartSummary: { height: undefined, opacity: undefined } };

    const headerAnimation = isHeaderCollapsed
      ? `${collapseHeader} 200ms ease-out forwards`
      : `${expandHeader} 200ms ease-out forwards`;

    const cartSummaryAnimations = {
      height: isCartSummaryCollapsed
        ? `${collapseCartSummary} 200ms ease-out forwards`
        : `${expandCartSummary} 200ms ease-out forwards`,

      opacity: isCartSummaryCollapsed
        ? `${fadeInCartSummary} 200ms ease-out forwards`
        : `${fadeOutCartSummary} 200ms ease-out forwards`,
    };

    return {
      header: headerAnimation,
      cartSummary: cartSummaryAnimations,
    };
  }, [showMobileBackground, isHeaderCollapsed, isCartSummaryCollapsed]);

  const exitIntentContextValue = useMemo(
    () => ({
      isOpen: exitIntent.isOpen,
      triggerExitIntent: exitIntent.triggerExitIntent,
      onLeave: exitIntent.onLeave,
      onReasonSelected: exitIntent.onReasonSelected,
      setPendingExitCallback: exitIntent.setPendingExitCallback,
    }),
    [exitIntent],
  );

  return (
    <ExitIntentContext.Provider value={exitIntentContextValue}>
      <Box id="just-form-layout" ref={portalRef}>
        <LoadingWrapper isLoading={loading} isPaidLoading={paidLoading}>
          <Box
            zIndex={1000}
            id="just-form-header"
            animation={animations.header}
            top={isMobile ? '0' : undefined}
            position={isMobile ? 'sticky' : 'relative'}
            bg={formBg}
            mb={'-2px'}
            boxShadow={isGlassy ? 'inset 0 -1px 0 rgba(255, 255, 255, 0.35)' : undefined}
          >
            <Header h={headerHeight} />
          </Box>
          <ProgressBar hide={!showProgressBar} progress={scrollPercentage} />
          <Flex
            bg={formBg}
            overflowY="hidden"
            h={showMobileBackground ? `calc(100% - ${headerHeight})` : undefined}
            boxShadow={isGlassy ? 'inset 0 1px 0 rgba(255, 255, 255, 0.25)' : undefined}
            position="relative"
          >
            <Divider
              orientation="vertical"
              hidden={!isLargerThan900}
              position="absolute"
              right="49%"
              {...(isClassicOrCustom
                ? { ...isClassDivierStyle }
                : { border: `0.8px solid ${dividerColor}`, borderStyle: 'dashed', h: '85%', top: '5.5rem' })}
            />
            <Container
              ref={mainRef}
              pb={contentPaddingBottom}
              pl={isLargerThan900 ? 10 : isLargerThan750 ? 8 : 2}
              pr={isLargerThan900 ? 10 : isLargerThan750 ? 8 : 2}
              maxW={isLargerThan900 ? '460px' : isLargerThan750 ? '344px' : 'full'}
              m="0"
              as="main"
              flex="1 1 auto"
              overflow="auto"
              height="100%"
              display="flex"
              flexDirection="column"
              pt={isMobile ? 2 : 3.5}
              w={isLargerThan900 ? '380px' : isLargerThan750 ? '300px' : 'full'}
              sx={scrollbarStyles}
            >
              {children}
            </Container>
            {!isMobile && (
              <Box
                backgroundColor={cartBg}
                borderRadius="1.25rem"
                px="12px"
                pb="12px"
                pt="6px"
                ml={isLargerThan900 ? '21px' : 0}
                mt="3.5"
                w="25.0625rem"
                mb="26px"
                display="flex"
                flexDirection="column"
                overflow="auto"
                h="80%"
                border={isGlassy ? '1px solid rgba(255, 255, 255, 0.28)' : undefined}
                boxShadow={
                  isGlassy
                    ? '0 10px 30px rgba(0, 0, 0, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.45)'
                    : undefined
                }
              >
                {useCartStatus !== 'success' && !cart ? (
                  <CartSkeleton showMobileBackground={showMobileBackground} />
                ) : (
                  <CartDetail />
                )}
              </Box>
            )}
          </Flex>
        </LoadingWrapper>
        <ExitIntentPopup
          isOpen={exitIntent.isOpen}
          onLeave={exitIntent.onLeave}
          onReasonSelected={exitIntent.onReasonSelected}
          shuffleOptions={exitIntent.shuffleOptions}
        />
      </Box>
    </ExitIntentContext.Provider>
  );
};
